import axios, { AxiosRequestHeaders } from "axios";
import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { getAuth } from "../../../../app/modules/auth";
import { KTSVG } from "../../../helpers";
import { toastfy } from "../../toast/Toast";
import { showErrorMessage } from "../../../helpers/MessagesHelper";
import { useNavigate } from "react-router-dom";
import { useStyleContext } from "../../../helpers/StyleContext";
import clsx from "clsx";

export interface modalSettingsInterface {
  itemId: number;
  deleteItemDescriptionOrName?: string;
  module: string;
  toBan?: boolean;
}

type BanModalProps = {
  modalSettings: modalSettingsInterface;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
  loadList: (module: string, headers: AxiosRequestHeaders) => Promise<void>;
};

const BanModal: React.FC<BanModalProps> = ({
  modalSettings,
  showModal,
  setShowModal,
  handleClose,
  loadList,
}) => {
  const navigate = useNavigate();
  const [disabledBanButton, setDisabledBanButton] = useState(false);
  const { styleState } = useStyleContext();
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiToken = getAuth();
  const token = apiToken?.api_token;

  const handleBan = (module: string, id: Number) => {
    setDisabledBanButton(true);

    const headers = {
      Authorization: `Bearer ${token}`,
      operation: "users/update",
    };

    const body = {
      banned: modalSettings.toBan,
    };

    axios
      .put(`${apiUrl}/${module}${id ? `/${id}` : ""}`, body, {
        headers,
      })
      .then((response) => {
        setShowModal(false);
        loadList(module, headers);
        setTimeout(() => {
          setDisabledBanButton(false);
        }, 1000);

        toastfy("success", "Operação bem sucedida!");
      })
      .catch((error) => {
        showErrorMessage(
          error?.response?.data?.error?.statusCode,
          error?.response?.data?.error?.message
        );

        if (error?.response?.data?.error?.statusCode === 403) {
          navigate("/");
        }
      });
  };

  return (
    <Modal show={showModal} onHide={handleClose} centered={true}>
      <style>
        {`
          .btn-close {
            position: absolute;
            right: 4%;
            top: 7%;
          }
          .modal-content {
            background-color: ${styleState?.secondary_color}
          }
        `}
      </style>
      <Modal.Header style={{ textAlign: "center" }} closeButton>
        <Modal.Title
          style={{ width: "100%", color: styleState.secondary_text_color }}
        >
          <KTSVG
            path="/media/icons/duotune/general/gen044.svg"
            className="svg-icon-5 svg-icon-5x svg-icon-warning"
          />
          <br />
          Atenção!
          <br />
          {modalSettings.toBan
            ? "Você deseja banir esse usuário?"
            : "Você deseja remover o ban desse usuário?"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modalSettings.toBan && (
          <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mb-4">
            Ao continuar com a operação esse usuário será banido e não poderá
            acessar mais as seções.
          </div>
        )}
        <table className="table align-middle gs-0 gy-4">
          <thead>
            <tr
              className={clsx("fw-bold", {
                "text-muted bg-light": !styleState.secondary_text_color,
              })}
              style={{
                backgroundColor: styleState?.secondary_text_color,
                color: styleState?.secondary_color,
              }}
            >
              <th
                key={0}
                className="ps-4 rounded-start"
                style={{ maxWidth: "30px" }}
              >
                #
              </th>
              <th key={1} className="ps-4 rounded-end">
                Nome
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span
                  className={clsx("fw-bold text-hover-secondary mb-1 fs-6", {
                    "text-dark": !styleState.secondary_text_color,
                  })}
                  style={{
                    color: styleState?.secondary_text_color,
                  }}
                >
                  {modalSettings.itemId}
                </span>
              </td>
              <td>
                <span
                  className={clsx("fw-bold text-hover-secondary mb-1 fs-6", {
                    "text-dark": !styleState.secondary_text_color,
                  })}
                  style={{
                    color: styleState?.secondary_text_color,
                  }}
                >
                  {modalSettings.deleteItemDescriptionOrName}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <span style={{ color: styleState.secondary_text_color }}>
          Para continuar, clique em banir ou para cancelar a operação clique em
          cancelar.
        </span>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={modalSettings.toBan ? "danger" : "success"}
          onClick={(event) => {
            event.preventDefault();
            handleBan(modalSettings.module, modalSettings.itemId);
          }}
          disabled={disabledBanButton}
        >
          <span style={{ display: disabledBanButton ? "none" : "block" }}>
            {modalSettings.toBan ? "Banir" : "Remover ban"}
          </span>
          <span
            className="indicator-progress"
            style={{ display: disabledBanButton ? "block" : "none" }}
          >
            {modalSettings.toBan ? "Banindo..." : "Removendo"}
            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
          </span>
        </Button>
        <Button variant="secondary" onClick={handleClose}>
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BanModal;
